import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery } from './pagination.js';
import { PartnerStatementSchema, StatementSideSchema } from './partners.js';
import { CurrencySchema, IsoDateSchema, MessageSchema, UnitPrecisionSchema, } from './primitives.js';
const c = initContract();
/**
 * PART 14 — the report centers (DSH-05 sales, DSH-06 purchases, DSH-07
 * inventory) on the DSH-10 skeleton, and DSH-08 financial statements.
 *
 * R9 / BR-DSH-10-01a: every figure counts POSTED documents only — a draft
 * never appears in a card, a chart, or a detail row.
 *
 * BR-DSH-10-04/-05: the comparison window is the same number of days ending
 * the day before the current window starts; previous 0 → +100.0 when current
 * is above zero, 0.0 when both are zero. Computed server-side so every
 * client agrees.
 *
 * R13 (DSH-10 Band 6): saved configurations are per user per report —
 * columns + filters + period, named. BR-DSH-10-23: never a grant.
 *
 * Permissions: reports.view on every route; per center the service also
 * requires the area whose documents it reads (sales.view / purchasing.view /
 * inventory.view / accounting.view) — BR-DSH-10-13.
 */
const SignedMoney = z.string().regex(/^-?\d{1,13}\.\d{2}$/);
/** A summary card: value, previous window value, change to 1 decimal. */
const CardSchema = z
    .object({
    value: SignedMoney,
    previous: SignedMoney,
    /** "+100.0" | "-76.2" | "0.0". */
    changePct: z.string(),
})
    .strict();
const CountCardSchema = z
    .object({ value: z.number().int(), previous: z.number().int(), changePct: z.string() })
    .strict();
const RankedRowSchema = z
    .object({ id: z.string().nullable(), name: z.string(), value: SignedMoney, count: z.number().int() })
    .strict();
/**
 * THE TWO CURRENCY FIELDS DSH-05, DSH-06 AND THE PRICE-TREND CARD CARRY.
 *
 * These reports total DOCUMENTS, and a document carries its own currency
 * (GEN-108). Every money figure on them is therefore stated in the company's
 * base currency, converted at the rate EACH DOCUMENT FROZE when it was
 * confirmed (GEN-109a/GEN-110/GEN-111; F-10: foreign→base divides) — the
 * books' convention, the one `arOpenSql`'s `unpaid_base` and the CRM-04
 * statement already use. Not DSH-04's as-of consolidation, which is right for
 * a cash board and would restate a closed period here every time a rate is
 * typed (GEN-112: "a later change to a rate must not restate any confirmed
 * document").
 *
 *   baseCurrency    what every figure below is stated in. Sent rather than
 *                   assumed, because the client cannot know it and 'USD' is
 *                   the literal that has been wrong before.
 *   conversionNote  GEN-115 / BR-MD-11-15's one-line disclosure, or NULL when
 *                   nothing converted (GEN-116) — which is most companies. It
 *                   names the BASIS rather than a rate date, because with
 *                   frozen rates there are as many dates as documents; the
 *                   sentence is `FX_MESSAGES.convertedAtFrozenRates` — one
 *                   sentence, so two screens cannot describe the same
 *                   arithmetic in two ways. The SERVER decides whether a
 *                   conversion happened, because only the server knows; and it
 *                   asks whether the COMPANY holds any foreign posted document
 *                   rather than whether this WINDOW did, so the line cannot go
 *                   quiet the moment a reader narrows the dates past the
 *                   foreign invoices while the figures stay converted.
 *
 * DSH-07 carries neither, and that is not an oversight: stock is valued in
 * the company's own money already (GEN-118, BR-MD-08-11), so it converts
 * nothing and GEN-116 keeps the line off. DSH-08 likewise — `trialBalance`
 * filters to the base currency (GEN-107).
 */
const convertedReportFields = {
    baseCurrency: z.string(),
    conversionNote: z.string().nullable(),
};
// ── DSH-05 Sales Reports ────────────────────────────────────────────────────
export const SalesReportSchema = z
    .object({
    ...convertedReportFields,
    summary: z.object({
        totalRevenue: CardSchema,
        invoices: CountCardSchema,
        avgInvoice: CardSchema,
        itemsSold: CountCardSchema,
    }),
    /** BR-DSH-05-05: one bar per month WITH data. */
    trend: z.array(z.object({ month: z.string(), revenue: SignedMoney, invoices: z.number().int() }).strict()),
    bySalesperson: z.array(z
        .object({ id: z.string().nullable(), name: z.string(), total: SignedMoney, invoices: z.number().int(), avg: SignedMoney })
        .strict()),
    byCategory: z.array(z
        .object({
        id: z.string().nullable(),
        name: z.string(),
        revenue: SignedMoney,
        /** Whole percent (BR-DSH-05-10). */
        share: z.number().int(),
        items: z.array(z.object({ name: z.string(), revenue: SignedMoney }).strict()),
    })
        .strict()),
    topCustomers: z.array(z
        .object({ id: z.string().nullable(), name: z.string(), total: SignedMoney, invoices: z.number().int(), lastPurchase: IsoDateSchema.nullable() })
        .strict()),
    topItems: z.array(z
        .object({ id: z.string().nullable(), code: z.string(), name: z.string(), qty: z.string(), revenue: SignedMoney })
        .strict()),
    byWarehouse: z.array(RankedRowSchema),
    /** The detail table — full column set; the column PICKER trims client-side (R13). */
    rows: z.array(z
        .object({
        invoiceId: z.uuid(),
        date: IsoDateSchema,
        invoiceNo: z.string(),
        customer: z.string(),
        salesperson: z.string(),
        warehouse: z.string(),
        lines: z.number().int(),
        /**
         * TWO FIGURES, `arOpenSql`'s shape. `total` is what the INVOICE
         * itself says, in `currency` — a reader searching for INV-0001 must
         * find the number printed on it. `totalBase` is the same amount at
         * the rate that invoice froze, and is the only one of the two that
         * may be added to anything (F-27). The cards above add exactly these
         * `totalBase` figures, which is what makes BR-DSH-05-09 hold.
         */
        total: SignedMoney,
        currency: z.string(),
        totalBase: SignedMoney,
        status: z.enum(['paid', 'partial', 'overdue', 'open']),
    })
        .strict()),
})
    .strict();
// ── DSH-06 Purchase Reports ─────────────────────────────────────────────────
export const PurchaseReportSchema = z
    .object({
    ...convertedReportFields,
    summary: z.object({
        totalSpend: CardSchema,
        bills: CountCardSchema,
        avgBill: CardSchema,
        itemsPurchased: CountCardSchema,
    }),
    trend: z.array(z.object({ month: z.string(), spend: SignedMoney, bills: z.number().int() }).strict()),
    bySupplier: z.array(z
        .object({
        id: z.string().nullable(),
        name: z.string(),
        total: SignedMoney,
        bills: z.number().int(),
        avg: SignedMoney,
        /** BR-DSH-06-03: whole percent, null (a dash) with no deliveries. */
        onTimePct: z.number().int().nullable(),
    })
        .strict()),
    byCategory: z.array(z
        .object({
        id: z.string().nullable(),
        name: z.string(),
        spend: SignedMoney,
        share: z.number().int(),
        items: z.array(z.object({ name: z.string(), spend: SignedMoney }).strict()),
    })
        .strict()),
    topItems: z.array(z
        .object({
        id: z.string().nullable(),
        code: z.string(),
        name: z.string(),
        qty: z.string(),
        spend: SignedMoney,
        /** BR-DSH-06-02: 2 decimals, never rounded to whole dollars. */
        avgUnitCost: SignedMoney,
    })
        .strict()),
    byWarehouse: z.array(RankedRowSchema),
    /**
     * §5a row 4's option list — the Purchased by filter (BR-DSH-06-11).
     * Everyone who has ever raised a POSTED bill, unwindowed and unfiltered
     * by the other dropdowns, so the picker cannot narrow itself: options
     * derived from the filtered rows would shrink to whoever survives the
     * current filters, and a reader could never widen back out through a
     * list that already forgot the others. ("Who raised the order" is
     * `created_by` — the same identity every figure on the page groups by.)
     */
    purchasers: z.array(z.object({ id: z.string(), name: z.string() }).strict()),
    rows: z.array(z
        .object({
        billId: z.uuid(),
        date: IsoDateSchema,
        billNo: z.string(),
        supplier: z.string(),
        purchasedBy: z.string(),
        warehouse: z.string(),
        lines: z.number().int(),
        /** The DSH-05 pair: what the bill says, and what may be added. */
        total: SignedMoney,
        currency: z.string(),
        totalBase: SignedMoney,
        status: z.enum(['paid', 'partial', 'overdue', 'open']),
    })
        .strict()),
})
    .strict();
/**
 * DSH-06 §5h — one point per purchase of one item, in date order.
 *
 * Every `price` is in `baseCurrency`, at the rate its own bill froze. On this
 * card the reason is not F-27's sum but F-98's DRIFT: `changePct` measures the
 * distance between the first point and the last, and a distance between two
 * currencies is exactly as meaningless as their total. Unconverted, an item
 * bought at 10.00 USD and later at 10.00 EUR reported 0.0% drift.
 */
export const PriceTrendSchema = z
    .object({
    ...convertedReportFields,
    points: z.array(z.object({ date: IsoDateSchema, price: SignedMoney }).strict()),
    /** BR-DSH-06-05/-07: (last−first)/first ×100 to 1dp; "0.0" when first is zero. */
    changePct: z.string(),
})
    .strict();
// ── DSH-07 Inventory Reports ────────────────────────────────────────────────
/**
 * BR-DSH-07-14: "A user without permission to see cost must be shown this
 * screen with the cost, value, turnover and ABC content removed, rather than
 * being refused the screen."
 *
 * REMOVED means absent from the payload, not blanked in it — the same fence
 * MD-03 §9 puts on an item's cost, and for the same reason: a figure that
 * reaches the browser has already left the building. `costVisible` is the
 * one flag the screen needs to know it is looking at the reduced report
 * rather than at an empty warehouse.
 */
export const InventoryReportSchema = z
    .object({
    /** False = the reduced screen (BR-DSH-07-14); the cost keys are absent. */
    costVisible: z.boolean(),
    /** BR-DSH-07-03: no change pills — stock is a state. */
    summary: z.object({
        totalValue: SignedMoney.optional(),
        skusTracked: z.number().int(),
        /** 1 decimal, null (em dash) when the denominator is zero. */
        avgTurnover: z.string().nullable().optional(),
        deadCount: z.number().int(),
    }),
    valuation: z.array(z
        .object({
        itemId: z.uuid(),
        code: z.string(),
        name: z.string(),
        category: z.string(),
        onHand: z.string(),
        /**
         * `N-4` — the item's stock unit and the decimals it allows. DSH-07's
         * valuation lists every item the company holds, in every unit it
         * holds them in, which is precisely `N-14`'s case: one column, many
         * units, no header that could be true of the whole of it.
         */
        unitSymbol: z.string(),
        unitPrecision: UnitPrecisionSchema,
        unitCost: SignedMoney.optional(),
        totalValue: SignedMoney.optional(),
        /**
         * BR-DSH-10-18 — the valuation method "must come from the warehouse
         * master record". Null when no single master record can answer:
         * under **All warehouses**, an item held at sites with different
         * methods has no one method, and the screen draws an em dash rather
         * than the report inventing a value no warehouse is configured with.
         */
        method: z.string().nullable(),
    })
        .strict()),
    valueByCategory: z
        .array(z.object({ id: z.string().nullable(), name: z.string(), value: SignedMoney, share: z.number().int() }).strict())
        .optional(),
    valueByWarehouse: z
        .array(z.object({ id: z.string().nullable(), name: z.string(), value: SignedMoney }).strict())
        .optional(),
    turnoverByCategory: z.array(z.object({ name: z.string(), turnover: z.string() }).strict()).optional(),
    /** BR-DSH-07-10: exactly four buckets, green/blue/amber/red. */
    aging: z.array(z
        .object({
        bucket: z.enum(['0-30', '31-60', '61-90', '90+']),
        value: SignedMoney.optional(),
        items: z.number().int(),
    })
        .strict()),
    deadStock: z.array(z
        .object({
        itemId: z.uuid(),
        code: z.string(),
        name: z.string(),
        onHand: z.string(),
        /** `N-4` — the same pair; dead stock is the same shelf, still counted. */
        unitSymbol: z.string(),
        unitPrecision: UnitPrecisionSchema,
        lastMovement: IsoDateSchema.nullable(),
        daysIdle: z.number().int(),
        tiedUpValue: SignedMoney.optional(),
    })
        .strict()),
    /** BR-DSH-07-11: running-total classes; counts sum to skusTracked. ABC
     *  ranks items BY VALUE, so it is one of the four blocks that leave with
     *  the cost (BR-DSH-07-14). */
    abc: z
        .array(z
        .object({
        itemId: z.uuid(),
        class: z.enum(['A', 'B', 'C']),
        code: z.string(),
        name: z.string(),
        value: SignedMoney,
        share: z.number().int(),
    })
        .strict())
        .optional(),
})
    .strict();
// ── DSH-08 Financial Reports ────────────────────────────────────────────────
export const StatementKindSchema = z.enum(['pnl', 'balance', 'cashflow']);
/**
 * One statement row. `kind` drives the styling; `varTone` is the
 * FAVOURABILITY colour computed server-side (BR-DSH-08-07/-08 — sign alone
 * gets four sections wrong).
 */
export const FinStatementRowSchema = z
    .object({
    kind: z.enum(['section', 'line', 'subtotal', 'total']),
    /** Null on Cash Flow rows (BR-DSH-08-13) and on sections/subtotals. */
    code: z.string().nullable(),
    accountId: z.uuid().nullable(),
    label: z.string(),
    labelAr: z.string().nullable(),
    current: SignedMoney.nullable(),
    comparison: SignedMoney.nullable(),
    /** Whole percent with explicit sign; null = em dash (BR-DSH-08-09/-14). */
    varPct: z.string().nullable(),
    varTone: z.enum(['good', 'bad', 'flat']).nullable(),
})
    .strict();
export const FinancialReportSchema = z
    .object({
    statement: StatementKindSchema,
    rows: z.array(FinStatementRowSchema),
    /** BR-DSH-08-15/-16/-17 — three tiles, em-dash on zero denominators. */
    ratios: z.array(z.object({ key: z.string(), value: z.string().nullable(), kind: z.enum(['pct', 'ratio', 'money']) }).strict()),
    /** Balance Sheet only (BR-DSH-08-04); null elsewhere. */
    balanced: z.boolean().nullable(),
    /**
     * D19 / GEN-115 — one line above the numbers naming any posted lines the
     * statement could not state in the base currency, and NULL when there are
     * none, which is GEN-116's "hidden when nothing happened".
     *
     * This is the honesty condition on D19's discard of pre-launch foreign
     * entries. It is null for every company created after that release and can
     * never become non-null for one: nothing can enter the excluded population
     * again once every posting door writes the equivalent.
     */
    exclusionNote: z.string().nullable(),
})
    .strict();
// ── R13 saved configurations ────────────────────────────────────────────────
export const REPORT_KINDS = ['sales', 'purchase', 'inventory', 'financial'];
export const ReportKindSchema = z.enum(REPORT_KINDS);
export const ReportConfigBodySchema = z
    .object({
    /** Ordered visible column ids of the detail table (BR-DSH-10-20). */
    columns: z.array(z.string().max(60)).max(30),
    /** Dropdown/typed filter values by filter id (BR-DSH-10-21). */
    filters: z.record(z.string().max(60), z.string().max(120)),
    /** Period preset + custom dates. */
    period: z
        .object({
        preset: z.string().max(20),
        from: IsoDateSchema.optional(),
        to: IsoDateSchema.optional(),
    })
        .strict(),
})
    .strict();
export const ReportConfigSchema = z
    .object({
    id: z.uuid(),
    report: ReportKindSchema,
    name: z.string(),
    config: ReportConfigBodySchema,
})
    .strict();
export const REPORT_MESSAGES = {
    /** Unique (user, report, name). */
    duplicateName: (name) => `You already have a saved configuration named ${name}.`,
    configNotFound: 'This saved configuration does not exist.',
    /** ERR-DSH-05-01's proposed sentence. */
    badWindow: 'The end date must be on or after the start date.',
};
/**
 * D46 — the saved-configuration name-clash refusal, structured. `name` is a
 * display value. Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const REPORT_REFUSALS = {
    duplicateName: (name) => ({
        message: REPORT_MESSAGES.duplicateName(name),
        key: 'reports.errDuplicateName',
        params: { name },
    }),
};
// ── The contract ────────────────────────────────────────────────────────────
const WindowQuery = z.object({ from: IsoDateSchema, to: IsoDateSchema });
export const reportsContract = c.router({
    sales: {
        method: 'GET',
        path: '/api/reports/sales',
        query: WindowQuery.extend({
            salespersonId: z.uuid().optional(),
            customerId: z.uuid().optional(),
            categoryId: z.uuid().optional(),
            warehouseId: z.uuid().optional(),
        }),
        responses: { 200: SalesReportSchema, 400: MessageSchema, 403: MessageSchema },
        summary: 'DSH-05 (reports.view + sales.view; posted invoices only — R9)',
    },
    purchases: {
        method: 'GET',
        path: '/api/reports/purchases',
        query: WindowQuery.extend({
            supplierId: z.uuid().optional(),
            categoryId: z.uuid().optional(),
            warehouseId: z.uuid().optional(),
            purchasedById: z.uuid().optional(),
        }),
        responses: { 200: PurchaseReportSchema, 400: MessageSchema, 403: MessageSchema },
        summary: 'DSH-06 (reports.view + purchasing.view; posted bills only — R9)',
    },
    purchasePriceTrend: {
        method: 'GET',
        path: '/api/reports/purchases/price-trend',
        /**
         * THE SAME FOUR FILTERS THE REPORT ITSELF TAKES, and they were absent.
         *
         * This query was `{ from, to, itemId }`, so §5h's drift card was the one
         * figure-producing endpoint on DSH-06 that no filter reached — pick a
         * supplier and the card still plotted every supplier's purchases of that
         * item, and printed a drift percentage measured across all of them.
         *
         * BR-DSH-06-11 names one of the four ("The Purchased by filter must limit
         * every figure on the page" — and since the DSH tail wave that control
         * EXISTS, so the ignore-marks that stood here are citations now), but the
         * scope question is not settled by that rule alone and does not need to
         * be: BR-DSH-10-07 governs every report screen, and says "changing ANY
         * filter must recompute EVERY card, chart and table on the page at once."
         * The drift card is a chart on the page. So all four travel, not just the
         * one BR-DSH-06-11 names.
         *
         * The picker above the card was ALREADY narrowed — it is built from the
         * report's own `topItems`, which is filtered — so the card was offering a
         * filtered list of items and plotting unfiltered data about whichever one
         * was chosen. That inconsistency is the shape of the defect: nothing on
         * screen looked wrong, and the number was about a different population
         * from the list it was picked out of.
         */
        query: WindowQuery.extend({
            itemId: z.uuid(),
            supplierId: z.uuid().optional(),
            categoryId: z.uuid().optional(),
            warehouseId: z.uuid().optional(),
            purchasedById: z.uuid().optional(),
        }),
        responses: { 200: PriceTrendSchema, 400: MessageSchema, 403: MessageSchema },
        summary: 'DSH-06 §5h — unit price drift for one item, under the page filters',
    },
    inventory: {
        method: 'GET',
        path: '/api/reports/inventory',
        query: z.object({
            warehouseId: z.uuid().optional(),
            categoryId: z.uuid().optional(),
            /** DSH-07 §5g: the typed threshold, default 60, strictly-greater-than. */
            threshold: z.coerce.number().int().min(1).max(3650).optional(),
        }),
        responses: { 200: InventoryReportSchema, 403: MessageSchema },
        summary: 'DSH-07 (reports.view + inventory.view). Without inventory.see_item_cost the cost, value, turnover and ABC blocks are ABSENT rather than the screen refused (BR-DSH-07-14).',
    },
    financial: {
        method: 'GET',
        path: '/api/reports/financial',
        query: WindowQuery.extend({
            statement: StatementKindSchema,
            /** BR-DSH-08-20: custom carries its own dates. */
            compare: z.enum(['previous', 'lastYear', 'custom', 'none']),
            compareFrom: IsoDateSchema.optional(),
            compareTo: IsoDateSchema.optional(),
        }),
        responses: { 200: FinancialReportSchema, 400: MessageSchema, 403: MessageSchema },
        summary: 'DSH-08 — P&L / Balance Sheet / Cash Flow from the ledger (reports.view + accounting.view)',
    },
    /**
     * CRM-03 — the partner statement as a REPORT CENTRE. Same builder as the
     * partner screen's CRM-04 statement (`partner-statement.ts`), so the two
     * cannot disagree about a closing balance; what this route adds is the
     * partner as a query parameter (BR-CRM-04-01's picker, on a screen of its
     * own). Posted documents only, like every statement (BR-DSH-10-01a).
     */
    partnerStatement: {
        method: 'GET',
        path: '/api/reports/statement',
        query: pagedQuery({
            partnerId: z.uuid(),
            side: StatementSideSchema.optional(),
            from: IsoDateSchema.optional(),
            to: IsoDateSchema.optional(),
            q: z.string().optional(),
            /** Which currency's statement; absent = the partner's own. */
            currency: CurrencySchema.optional(),
        }),
        responses: {
            200: PartnerStatementSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: "CRM-03 — one partner's statement under Reports (reports.view + crm.view): the CRM-04 builder with the partner, the view and the window as parameters. PAGED (NFR-42); the cards and the aging bands cover the whole ledger (BR-CRM-04-13).",
    },
    listConfigs: {
        method: 'GET',
        path: '/api/reports/configs',
        query: z.object({ report: ReportKindSchema }),
        responses: { 200: z.object({ configs: z.array(ReportConfigSchema) }).strict() },
        summary: 'R13: this user’s saved configurations for one report',
    },
    createConfig: {
        method: 'POST',
        path: '/api/reports/configs',
        body: z
            .object({ report: ReportKindSchema, name: z.string().min(1).max(80), config: ReportConfigBodySchema })
            .strict(),
        responses: { 201: ReportConfigSchema, 409: MessageSchema },
        summary: 'R13: save a named configuration (unique per user+report+name)',
    },
    updateConfig: {
        method: 'PATCH',
        path: '/api/reports/configs/:configId',
        pathParams: z.object({ configId: z.uuid() }),
        body: z
            .object({ name: z.string().min(1).max(80).optional(), config: ReportConfigBodySchema.optional() })
            .strict(),
        responses: { 200: ReportConfigSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'R13: rename or overwrite a saved configuration',
    },
    deleteConfig: {
        method: 'DELETE',
        path: '/api/reports/configs/:configId',
        pathParams: z.object({ configId: z.uuid() }),
        body: c.noBody(),
        responses: { 204: c.noBody(), 404: MessageSchema },
        summary: 'R13: delete a saved configuration',
    },
});
//# sourceMappingURL=reports.js.map